import { EventWidgetCard } from "shared/components/organisms/EventWidgetCard";
import { Box, Grid } from "styled-system/jsx";
import { useEventsWidgetContext } from "../EventsContext";
import { DateRange, EventWidgetSize } from "widgets/events/types";
import { eventsRecipe } from "./recipe";
import { EventManyQuery } from "gql/generated";
import { useMemo } from "react";
import { Skeleton } from "shared/components";
import { EventMessageCard } from "../EventMessageCard";
import { format, parse } from "date-fns";
import { EVENT_DATE_FORMAT } from "shared/utils";
import { useTranslation } from "react-i18next";

type EventsProps = {
  events: EventManyQuery["eventMany"][0][];
  currentPage: number;
  eventsPerPage: number;
  dateRange: DateRange;
};

export const Events = ({
  events,
  currentPage,
  eventsPerPage,
  dateRange,
}: EventsProps) => {
  const { t } = useTranslation("events", { keyPrefix: "WIDGET" });
  const { locale, widgetSize } = useEventsWidgetContext();
  const classes = eventsRecipe({ size: widgetSize });

  // When the future event notice is shown, we show one fewer event on the first page
  // So if we would normally be showing 6 events, we show 0 to 4 on page 1 (5 items + the future event notice),
  // 5 to 10 on page 2 (6 items), etc.
  const futureEventsSliceStartOffset =
    dateRange.showFutureEventNotice && currentPage > 1 ? 1 : 0;
  const futureEventsSliceEndAndSkeletonsOffset =
    dateRange.showFutureEventNotice && currentPage === 1 ? 1 : 0;

  const eventsToShow = useMemo(() => {
    const startSliceAt =
      (currentPage - 1) * eventsPerPage - futureEventsSliceStartOffset;
    const endSliceAt =
      startSliceAt + eventsPerPage - futureEventsSliceEndAndSkeletonsOffset;
    return events.slice(startSliceAt, endSliceAt);
  }, [
    events,
    currentPage,
    eventsPerPage,
    futureEventsSliceStartOffset,
    futureEventsSliceEndAndSkeletonsOffset,
  ]);

  // If there aren't enough events to fill the page, we need to show skeletons
  // For cases, where the future event notice is shown, we need to show one fewer skeleton on the first page
  const skeletonsToShow = useMemo(() => {
    return Array.from({
      length:
        eventsPerPage -
        eventsToShow.length -
        futureEventsSliceEndAndSkeletonsOffset,
    });
  }, [eventsPerPage, eventsToShow, futureEventsSliceEndAndSkeletonsOffset]);

  return (
    <Grid w="full" overflowX="hidden" position="relative">
      <Grid className={classes.grid}>
        {/* Future event notice is ONLY shown on the first page */}
        {dateRange.showFutureEventNotice && currentPage === 1 && (
          <EventMessageCard
            text={t("NO_DATA.FUTURE_EVENTS", {
              month: format(
                parse(dateRange.from, EVENT_DATE_FORMAT, new Date()),
                "MMMM yyyy",
                { locale },
              ),
            })}
          />
        )}
        {eventsToShow.map((event) => (
          <EventWidgetCard
            key={event._id}
            event={event}
            orientation={
              widgetSize === EventWidgetSize.Mobile ? "portrait" : "landscape"
            }
          />
        ))}
        {skeletonsToShow.map((_, i) => (
          <Skeleton
            part="card__skeleton"
            key={i}
            className={classes.skeleton}
            mode="unset"
          />
        ))}
      </Grid>
      {widgetSize === EventWidgetSize.Mobile && (
        <>
          <Box part="featured__fader-left" className={classes.faderLeft} />
          <Box part="featured__fader-right" className={classes.faderRight} />
        </>
      )}
    </Grid>
  );
};
